import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  ButtonProps,
  InputSelectProps,
  InputTextProps,
  OptionProps,
  Text,
} from "@clearabee/ui-library-native";
import { isEvent } from "@clearabee/ui-library-base";
import { Input } from "components/Core/Input/Input";
import { HookComponent } from "@clearabee/ui-sdk";
import { Button } from "components/Core/Button/Button";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import type ReactSelect from "react-select";

export type DropdownProps = Omit<InputSelectProps, "options">;

export interface DropdownProp {
  (props: DropdownProps): React.ReactElement;
}

export interface InputTextComponent {
  (props: InputTextComponentProps): React.ReactElement;
}

type InputTextComponentProps = { buttonProps?: ButtonProps } & InputTextProps;

interface UseDropdownLookupProps {
  onChange: (
    text: string | React.ChangeEvent<HTMLInputElement>,
  ) => Promise<OptionProps[]>;
}

interface DropdownLookupProps extends UseDropdownLookupProps {
  children: (childrenProps: {
    Dropdown: DropdownProp;
    InputText: InputTextComponent;
    isLoading: boolean;
  }) => React.ReactElement;
}

interface UseDropdownLookupReturn {
  Dropdown: DropdownProp;
  InputText: InputTextComponent;
  isLoading: boolean;
  hasAddresses: boolean;
  handleSubmit: (postcode: string) => Promise<void>;
  dropdownValue: string | undefined;
  setDropdownValue: (value: string | undefined) => void;
}

export const useDropdownLookup = ({
  onChange,
}: UseDropdownLookupProps): UseDropdownLookupReturn => {
  const [items, setItems] = useState<OptionProps[]>([]);
  const [dropdownValue, setDropdownValue] = useState<string>();
  const [isLoading, setLoading] = useState(false);
  const [translate] = useTranslation();
  const inputSelect = useRef<ReactSelect>(null);

  const Dropdown = (props: DropdownProps) =>
    useMemo(() => {
      return (
        <Input.Select
          {...props}
          ref={inputSelect}
          options={items}
          openMenuOnFocus
          usePortal
          onChange={(value) => {
            props.onChange?.(value);
            setDropdownValue(isEvent(value) ? value.target.value : value);
          }}
          defaultValue={dropdownValue || props.defaultValue}
        />
      );
    }, [items, props]);

  const handleSubmit = async (
    postcode: string,
    preventAutoFocus?: boolean,
  ): Promise<void> => {
    setLoading(true);

    let options: OptionProps[] = [];
    try {
      options = (await onChange(postcode)) ?? [];
    } catch {}

    setItems(options);
    setLoading(false);

    if (!preventAutoFocus && options.length) {
      setTimeout(() => {
        inputSelect.current?.focus();
      });
    }
  };

  const InputText = useCallback(
    ({ buttonProps, ...props }: InputTextComponentProps) => {
      return (
        <Input.ButtonText>
          <Input.Text
            {...props}
            onChange={(value) => {
              props.onChange?.(value);
              setItems([]);
              setDropdownValue(undefined);
            }}
            onSubmitEditing={() => handleSubmit(props?.value ?? "")}
          />
          <Button
            {...buttonProps}
            color="warning"
            size="small"
            onClick={() => handleSubmit(props?.value ?? "")}
            type="button"
            disabled={isLoading}
            {...{
              [Platform.OS === "web" ? "data-testid" : "testID"]:
                "find-address",
            }}
          >
            <Text style={{ fontFamily: "ProximaSemiBold" }}>
              {translate("hwa.components.dropdownLookup.findAddress")}
            </Text>
          </Button>
        </Input.ButtonText>
      );
    },
    [isLoading],
  );

  return {
    Dropdown,
    InputText,
    isLoading,
    hasAddresses: !!items.length,
    dropdownValue,
    setDropdownValue,
    handleSubmit,
  };
};

export const DropdownLookup = ({
  children,
  ...props
}: DropdownLookupProps): React.ReactElement => {
  return HookComponent({
    hook: useDropdownLookup,
    children,
    ...props,
  });
};
